import React, { useState, useEffect, useRef, Fragment, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Marker, Circle, Polygon, OverlayView } from '@react-google-maps/api';

import {
    selectStartPoint, selectEndPoint, selectViaPoints, selectCurrentlyEditingPointType,
    selectComputeRoute, selectWorkingLatLng, selectWorkingMarkerId, selectWorkingZoneId,
    setComputeRoute, routeBuilt,
} from '../../../features/tripcenter/googleRouteMakerSlice';
import {
    setWorkingZoneId, setWorkingMarkerId, setWorkingAddress, setWorkingLatLng, getWorkingAddress
} from '../../../features/tripcenter/googleRouteMakerSlice';
import { zonesSelector, zonesListSelector } from '../../../features/places/placesSlice';
import { markersSelector, markersListSelector } from '../../../features/places/placesSlice';
import { liveTrackinglayoutSelector } from '../../../features/layout/layoutSlice';


import { setMapControl, setZoom, toggleVehicleIcons, setNeedBounding } from '../../../features/map/mapSlice';
import {
    clearVisibleAssetsMarkers, restoreVisibleAssetsMarkers, removeDetailView
} from '../../../features/assets/assetsSlice';

import GoogleMapSearch from './GoogleMapSearch';
import './googleSearchStyle.css';
import { Paper, Grid } from '@mui/material';


const GoogleRouteMaker = ({ map }) => {
    const dispatch = useDispatch();
    const dimentions = useSelector(liveTrackinglayoutSelector);
    //console.log("GoogleRouteMaker running", map);
    const directionsService = useMemo(() => new window.google.maps.DirectionsService(), []);

    const directionsRenderer = useMemo(() => {
        const renderer = new window.google.maps.DirectionsRenderer({
            draggable: true,
            suppressMarkers: true,
            map,
        });

        renderer.addListener("directions_changed", () => {
            //console.log("route recompute on drag");
            const directions = renderer.getDirections();
            if (directions) {
                //console.log("Route recomputed", directions);
                const { legs, overview_path } = directions.routes[0];
                if (legs && overview_path) {
                    let distance = 0;
                    let time = 0;
                    let path = [];
                    legs.forEach((leg) => {
                        distance += leg.distance.value;
                        time += leg.duration.value;
                    });
                    overview_path.forEach((point) => {
                        path.push({ lat: point.lat(), lng: point.lng() });
                    });
                    dispatch(routeBuilt({ path, distance, time }));
                }
            }
        });

        return renderer;
    }, [map, dispatch]);

    useEffect(() => {
        dispatch(setMapControl('route'));
        dispatch(removeDetailView());
        dispatch(clearVisibleAssetsMarkers());

        dispatch(toggleVehicleIcons(true));
        dispatch(setZoom(10));
        dispatch(setNeedBounding(true));
        return () => {
            //console.log("Component Unmounting");
            dispatch(setMapControl('asset'));
            dispatch(restoreVisibleAssetsMarkers());
            dispatch(toggleVehicleIcons(true));
            dispatch(setNeedBounding(false));
            // Clean up map drawings on unmount
            if (map) {
                // Get all overlays currently on the map
                const overlays = map.getOverlays?.();
                if (overlays) {
                    // Remove each overlay
                    overlays.forEach(overlay => {
                        if (overlay) {
                            //console.log("Clearing overlay", overlay);
                            overlay.setMap(null);
                        }
                    });
                }
            }
            // Clear directions renderer
            if (directionsRenderer) {
                //console.log("Clearing directions renderer");
                directionsRenderer.setDirections(null);
                directionsRenderer.setMap(null);
            }
        }
    }, [dispatch, map, directionsRenderer]);

    const generateSvgMarker = (color, geometry, google) => {
        // SVG paths for different geometries (centered around 0,0 in a 10x10 viewport)
        const paths = {
            square: "M-5,-5 L5,-5 L5,5 L-5,5 Z",
            hexagon: "M-3,-5 L3,-5 L6,0 L3,5 L-3,5 L-6,0 Z",
            star: "M0,-5 L1.5,-1.5 L5,-1.5 L2.5,1 L3.5,5 L0,2.5 L-3.5,5 L-2.5,1 L-5,-1.5 L-1.5,-1.5 Z"
        };

        // Validate geometry parameter
        if (!paths[geometry]) {
            return {
                path: paths[geometry],
                fillColor: color,
                fillOpacity: 1,
                strokeColor: 'white',
                strokeWeight: 2,
                rotation: 0,
                scale: 2,
                // Center anchor point for all shapes
                anchor: new google.maps.Point(0, 0)
            };
        }

        return {
            path: paths[geometry],
            fillColor: color,
            fillOpacity: 1,
            strokeColor: 'white',
            strokeWeight: 2,
            rotation: 0,
            scale: 2,
            // Center anchor point for all shapes
            anchor: new google.maps.Point(0, 0)
        };
    };

    const startPoint = useSelector(selectStartPoint);
    const endPoint = useSelector(selectEndPoint);
    const viaPoints = useSelector(selectViaPoints);
    const computeRoute = useSelector(selectComputeRoute);

    const currentlyEditingPointType = useSelector(selectCurrentlyEditingPointType);

    const workingLatLng = useSelector(selectWorkingLatLng);
    const workingZoneId = useSelector(selectWorkingZoneId);
    const workingMarkerId = useSelector(selectWorkingMarkerId);
    const zoneData = useSelector(state => state.places.zones[`${workingZoneId}`]);

    const calcRoute = useMemo(() => {
        if (computeRoute && startPoint && endPoint && directionsService && directionsRenderer) {
            //console.log("Working latlng", startPoint, endPoint, directionsService, directionsRenderer);
            dispatch(setComputeRoute(false));
            let directionQuery = {
                origin: new window.google.maps.LatLng(startPoint.lat, startPoint.lng),
                destination: new window.google.maps.LatLng(endPoint.lat, endPoint.lng),
                travelMode: window.google.maps.TravelMode.DRIVING,
                waypoints: [],
            };
            if (viaPoints.length > 0) {
                viaPoints.forEach((viaPoint, index) => {
                    directionQuery.waypoints.push({
                        location: new window.google.maps.LatLng(viaPoint.lat, viaPoint.lng),
                        stopover: true,
                    });
                });
            }
            //console.log("Direction query", directionQuery);
            directionsService
                .route(directionQuery)
                .then((response) => {
                    directionsRenderer.setDirections(response);

                    //console.log("Route computed", response);
                    const { legs, overview_path } = response.routes[0];
                    if (legs && overview_path) {
                        let distance = 0;
                        let time = 0;
                        let path = [];
                        legs.forEach((leg) => {
                            distance += leg.distance.value;
                            time += leg.duration.value;
                        });
                        overview_path.forEach((point) => {
                            path.push({ lat: point.lat(), lng: point.lng() });
                        });
                        dispatch(routeBuilt({ path, distance, time }));
                    }
                })
                .catch((e) => {
                    dispatch(setComputeRoute(false));
                    window.alert("Directions request failed due to " + e)
                });
        } else {
            //console.log("Some points or service not ready");
        }
    }, [computeRoute, dispatch, startPoint, endPoint, viaPoints, directionsService, directionsRenderer]);

    useEffect(() => {
        if (computeRoute) {
            calcRoute();
        }
    }, [computeRoute, calcRoute])

    useEffect(() => {
        if (workingLatLng && workingLatLng.lat && workingLatLng.lng) {
            dispatch(getWorkingAddress({ searchParam: workingLatLng }));
        }
    }, [workingLatLng, dispatch])


    useEffect(() => {
        const mapBounder = () => {
            if (map && workingLatLng) {
                if (workingLatLng.lat && workingLatLng.lng) {
                    let bounds = new window.google.maps.LatLngBounds();
                    let point = new window.google.maps.LatLng(
                        parseFloat(workingLatLng.lat), parseFloat(workingLatLng.lng)
                    );
                    bounds.extend(point);
                    map.fitBounds(bounds, { left: 350, bottom: 100, top: 75, right: 25 });
                    map.setZoom(15);
                    dispatch(setNeedBounding(false));
                }
            }
        };
        mapBounder();
    }, [workingLatLng, dispatch, map]);

    const relocateVertex = (e) => {
        dispatch(setWorkingLatLng({
            lat: e.latLng.lat(),
            lng: e.latLng.lng()
        }));
        dispatch(setWorkingZoneId(null));
        dispatch(setWorkingMarkerId(null));
        dispatch(getWorkingAddress({
            searchParam: {
                lat: e.latLng.lat(),
                lng: e.latLng.lng()
            }
        }));
    }   

    const [googleAddrUsed, setGoogleAddrUsed] = useState(false);
    const autoCompleteRef = useRef();
    const inputRef = useRef();
    useEffect(() => {
        if (!window.google) {
            //console.log("first load of google maps fail");
        } else {
            const options = {
                componentRestrictions: { country: "in" },
                fields: ["address_components", "geometry", "name"],
                types: ["establishment"]
            };
            if (inputRef.current !== undefined) {
                autoCompleteRef.current = new window.google.maps.places.Autocomplete(
                    inputRef.current,
                    options
                );
                autoCompleteRef.current.addListener("place_changed", async function () {
                    const place = await autoCompleteRef.current.getPlace();
                    //console.log("Place Got", place);
                    const { name, address_components, geometry, vicinity } = place;
                    //console.log("first load of google maps success", name, address_components, geometry, vicinity);
                    const lat = geometry.location.lat();
                    const lng = geometry.location.lng();
                    const addressText = address_components.reduce((acc, cur) => { return acc + " " + cur.long_name }, "");
                    setGoogleAddrUsed(true);
                    dispatch(setWorkingAddress({ address_components, name, addressText }));
                    dispatch(setWorkingLatLng({ lat: lat, lng: lng }));
                    dispatch(setWorkingZoneId(null));
                    dispatch(setWorkingMarkerId(null));
                });
            } else {
                //console.log("Input ref not ready");
            }

        }
    }, [dispatch, inputRef, autoCompleteRef]);

    const zones = useSelector(zonesSelector);
    //console.log("Zones", zones);
    const zoneNames = Object.keys(zones).map(zoneId => zones[zoneId].zone_name);
    const zoneIdList = useSelector(zonesListSelector);
    const [selectedZoneName, setSelectedZoneName] = useState("");
    const onZoneSelect = (e, newValue) => {
        //console.log("OZS", newValue, e.target.value, e.target);
        if (!newValue) {
            dispatch(setWorkingLatLng(null));
            dispatch(setWorkingZoneId(null));
            dispatch(setWorkingMarkerId(null));
        } else {
            let zoneidSelect = zoneIdList.find(zoneId => zones[zoneId].zone_name === newValue);
            if (zoneidSelect === undefined) {
                zoneidSelect = null;
            } else {
                setSelectedZoneName(newValue);
                setGoogleAddrUsed(false);
                let coords = zones[zoneidSelect].zone_coords;
                const centerLat = coords.reduce((sum, coord) => sum + coord.lat, 0) / coords.length;
                const centerLng = coords.reduce((sum, coord) => sum + coord.lng, 0) / coords.length;
                dispatch(setWorkingLatLng({ lat: centerLat, lng: centerLng }));
                dispatch(setWorkingZoneId(zoneidSelect));
                dispatch(setWorkingMarkerId(null));
                //dispatch(setWorkingAddress({ name: zones[zoneidSelect].zone_name }));
            }
        }
    }

    const zonePath = () => {
        let path = [];
        if (zoneData) {
            zoneData.zone_coords.forEach(z => {
                path.push({
                    lat: parseFloat(z.lat),
                    lng: parseFloat(z.lng),
                })
            });
        }
        return path;
    }

    const markers = useSelector(markersSelector);
    const markerNames = Object.keys(markers).map(markerId => markers[markerId].marker_name);
    const markerIdList = useSelector(markersListSelector);
    const [selectedMarkerName, setSelectedMarkerName] = useState("");

    const onMarkerSelect = (e, newValue) => {
        //console.log("OMS", newValue, e.target.value, e.target);
        if (!newValue) {
            dispatch(setWorkingLatLng(null));
            dispatch(setWorkingZoneId(null));
            dispatch(setWorkingMarkerId(null));
            //dispatch(setWorkingAddress(null));
        } else {
            let markeridSelect = markerIdList.find(markerId => markers[markerId].marker_name === newValue);
            if (markeridSelect === undefined) {
                markeridSelect = null;
            } else {
                setSelectedMarkerName(newValue);
                setGoogleAddrUsed(false);
                //console.log("Marker selceted", markeridSelect, markers[markeridSelect]);
                let lat = markers[markeridSelect].marker_coords.lat;
                let lng = markers[markeridSelect].marker_coords.lng;
                dispatch(setWorkingMarkerId(markeridSelect));
                dispatch(setWorkingLatLng({ lat: lat, lng: lng }));
                dispatch(setWorkingZoneId(null));
                //dispatch(setWorkingAddress({ name: markers[markeridSelect].marker_name }));
            }
        }
    }

    const workingMarkerDisplay = () => {
        if (currentlyEditingPointType && workingLatLng && workingLatLng.lat && workingLatLng.lng) {
            return (
                <Fragment>
                    {/* Better looking marker */}
                    <Marker
                        position={workingLatLng}
                        draggable={true}
                        onDragEnd={(e) => relocateVertex(e)}
                        icon={{
                            path: window.google.maps.SymbolPath.CIRCLE,
                            fillColor: "#f50057",
                            fillOpacity: 1,
                            strokeWeight: 2,
                            strokeColor: "#FFFFFF",
                            scale: 12,
                        }}
                        zIndex={1000}
                    />

                    {/* Inner pulse animation circle */}
                    <Circle
                        center={workingLatLng}
                        radius={15}
                        options={{
                            strokeColor: "#f50057",
                            strokeOpacity: 0.8,
                            strokeWeight: 2,
                            fillColor: "#f50057",
                            fillOpacity: 0.3,
                            zIndex: 999,
                        }}
                    />

                    {/* Outer highlight circle */}
                    <Circle
                        center={workingLatLng}
                        radius={200}
                        options={{
                            strokeColor: "#f50057",
                            strokeOpacity: 0.8,
                            strokeWeight: 2,
                            fillColor: "#f50057",
                            fillOpacity: 0.05,
                            zIndex: 998,
                        }}
                    />

                    {/* Always visible text label */}
                    <OverlayView
                        position={workingLatLng}
                        mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                        getPixelPositionOffset={(width, height) => ({
                            x: -(width / 2),
                            y: -height - 35 // Position above the marker
                        })}
                    >
                        <div style={{
                            background: 'rgba(0, 0, 0, 0.0)',
                            color: '#ffffff',
                            padding: '6px 10px',
                            borderRadius: '16px',
                            fontSize: '13px',
                            fontWeight: 'bold',
                            boxShadow: '0 2px 6px rgba(0,0,0,0)',
                            whiteSpace: 'nowrap',
                            pointerEvents: 'none', // Make it non-interactive so clicks pass through
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}>
                            <span style={{ marginRight: '5px' }}>⟲</span>
                            <span style={{ backgroundColor: 'rgba(128, 128, 128, 0.5)', boxShadow: '0 2px 6px rgba(0,0,0,0.3)' }}>Drag to adjust</span>
                        </div>
                    </OverlayView>
                </Fragment>
            );
        } else {
            return null;
        }
    }

    const zoneDisplay = () => {
        if (workingZoneId && zoneData && workingLatLng && workingLatLng.lat && workingLatLng.lng) {
            return (
                <Fragment>
                    <Circle
                        center={workingLatLng}
                        radius={10}
                        options={{
                            fillColor: "#483D8B",
                            fillOpacity: 0.5,
                            strokeColor: "#483D8B",
                            strokeOpacity: 0.4,
                            strokeWeight: 1,
                        }}
                    />
                    <Polygon
                        paths={zonePath()}
                        options={{
                            strokeWeight: 1,
                            strokeColor: "#6495ED",
                            fillOpacity: 0.1,
                            fillColor: "#6495ED",
                        }}
                    />
                </Fragment>
            );
        } else {
            return null;
        }
    };


    const markerDisplay = () => {
        if (workingMarkerId && workingLatLng && workingLatLng.lat && workingLatLng.lng) {
            return (
                <Fragment>
                    <Circle
                        center={workingLatLng}
                        radius={50}
                        options={{
                            fillColor: "#483D8B",
                            fillOpacity: 0.5,
                            strokeColor: "#483D8B",
                            strokeOpacity: 0.4,
                            strokeWeight: 1,
                        }}
                    />
                </Fragment>
            );
        } else {
            return null;
        }
    };

    const googleAddressDisplay = () => {
        if (googleAddrUsed && workingLatLng && workingLatLng.lat && workingLatLng.lng) {
            return (
                <Fragment>
                    <Circle
                        center={workingLatLng}
                        radius={10}
                        options={{
                            fillColor: "#483D8B",
                            fillOpacity: 0.5,
                            strokeColor: "#483D8B",
                            strokeOpacity: 0.4,
                            strokeWeight: 1,
                        }}
                    />
                    <Circle
                        center={workingLatLng}
                        radius={500}
                        options={{
                            fillColor: "#6495ED",
                            fillOpacity: 0.2,
                            strokeColor: "#6495ED",
                            strokeOpacity: 0.4,
                            strokeWeight: 1,
                        }}
                    />
                </Fragment>

            );
        } else {
            return null;
        }
    };

    const startPointDisplay = () => {
        if (startPoint && startPoint.lat && startPoint.lng) {
            return (
                <Fragment>
                    <Marker
                        icon={generateSvgMarker("DodgerBlue", "hexagon", window.google)}
                        position={startPoint}
                        onClick={() => { }}
                    />
                    <Circle
                        center={startPoint}
                        radius={200}
                        options={{
                            strokeColor: "DodgerBlue",
                            strokeOpacity: 1,
                            strokeWeight: 1,
                            fillColor: "DodgerBlue",
                            fillOpacity: 0.25,
                        }}
                    />
                </Fragment>
            );
        } else {
            return null;
        }
    };

    const endPointDisplay = () => {
        if (endPoint && endPoint.lat && endPoint.lng) {
            return (
                <Fragment>
                    <Marker
                        position={endPoint}
                        onClick={() => { }}
                        icon={generateSvgMarker("Navy", "hexagon", window.google)}
                    />
                    <Circle
                        center={endPoint}
                        radius={200}
                        options={{
                            strokeColor: "Navy",
                            strokeOpacity: 1,
                            strokeWeight: 1,
                            fillColor: "Navy",
                            fillOpacity: 0.25,
                        }}
                    />
                </Fragment>
            );
        } else {
            return null;
        }
    };

    const viaPointsDisplay = () => {
        if (viaPoints.length > 0) {
            return viaPoints.map((viaPoint, index) => {
                return (
                    <Fragment key={index}>
                        <Marker
                            position={viaPoint}
                            onClick={() => { }}
                            icon={generateSvgMarker("Orchid", "square", window.google)}
                        />
                        <Circle
                            center={endPoint}
                            radius={200}
                            options={{
                                strokeColor: "Orchid",
                                strokeOpacity: 1,
                                strokeWeight: 1,
                                fillColor: "Orchid",
                                fillOpacity: 0.25,
                            }}
                        />
                    </Fragment>
                );
            });
        } else {
            return null;
        }
    };

    return (
        <div>
            <div style={{ ...dimentions.googleRouterTop }}>
                {/* <Paper style={{ padding: '5px', margin: '5px', maxWidth: '800px', backgroundColor: 'White' }}>
                    <Grid container spacing={1} justifyContent="start">
                        <Grid item sm={10}>
                            <div style={{ marginLeft: '5px', backgroundColor: 'White' }}>
                                <div style={{ marginTop: '5px' }}>
                                    <input
                                        ref={inputRef}
                                        type="text"
                                        placeholder="Search Address in Google Maps"
                                        style={{
                                            marginTop: '14px', width: '100%', height: '100%', padding: '5px',
                                            fontSize: '16px', border: 'none', borderBottom: '1px solid grey', marginRight: '10px',
                                            backgroundColor: 'White',
                                        }}
                                    />
                                </div>
                            </div>
                        </Grid>
                    </Grid>
                </Paper> */}
                <GoogleMapSearch />
            </div>
            {workingMarkerDisplay()}
            {zoneDisplay()}
            {markerDisplay()}
            {googleAddressDisplay()}
            {startPointDisplay()}
            {endPointDisplay()}
            {viaPointsDisplay()}
        </div>
    );
};

export default GoogleRouteMaker;
